/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { AccessionPrefix } from './AccessionPrefix';
import type { BillCacheAdmin } from './BillCacheAdmin';
import type { Biller } from './Biller';
import type { Block } from './Block';
import type { CaseDataEntry } from './CaseDataEntry';
import type { CaseDiagnosis } from './CaseDiagnosis';
import type { CaseDocument } from './CaseDocument';
import type { CaseEventLog } from './CaseEventLog';
import type { CaseQAReview } from './CaseQAReview';
import type { CaseReportSignature } from './CaseReportSignature';
import type { CaseSlideAdmin } from './CaseSlideAdmin';
import type { CaseSpecimenAdmin } from './CaseSpecimenAdmin';
import type { CaseStatusLog } from './CaseStatusLog';
import type { CliaCertificate } from './CliaCertificate';
import type { Lab } from './Lab';
import type { LabOrder } from './LabOrder';
import type { PatientFullInsurance } from './PatientFullInsurance';
import type { ScreenRequest } from './ScreenRequest';
import type { UserContact } from './UserContact';
import type { UserCredentials } from './UserCredentials';
import type { UserName } from './UserName';

export type CaseAdmin = {
    /**
     * The Accession Number of the Case
     */
    readonly accession?: string | null;
    /**
     * date/time that we received a WPL order message indicating accessioning was completed
     */
    accession_completed?: string | null;
    readonly accession_prefix?: AccessionPrefix;
    readonly accessioning_lab?: Lab;
    readonly active_assignment?: UserCredentials;
    /**
     * case billing type: Third-Party Bill, Client Bill, Cash Pay, or No Bill
     */
    billing_type?: CaseAdmin.billing_type | null;
    /**
     * True if the patient is paying cash; i.e. no insurance. Aka self-pay
     */
    self_pay?: boolean;
    /**
     * True if the patient is paying cash with an attestation; i.e. no insurance. Aka self-pay with attestation
     */
    self_pay_with_attestation?: boolean;
    /**
     * True if the patient has an indigent pay agreement.
     */
    indigent?: boolean;
    readonly blocks?: Array<Block>;
    readonly case_addon?: string;
    readonly case_diagnosis?: Array<CaseDiagnosis>;
    readonly clinic_preferences?: string;
    /**
     * Changing this value will NOT modify the collected_date for each specimen.
     */
    collected_date?: string | null;
    readonly collected_date_offset?: string;
    readonly collected_date_timezone?: string;
    readonly created_at?: string;
    readonly documents?: Array<CaseDocument>;
    /**
     * A secondary or external accession for the same case from the system that provided the data to WPL, e.g. Poplar, RPA.
     */
    external_accession?: string | null;
    /**
     * Order id get from order message from third party, e.g. ModMed
     */
    external_order_id?: string | null;
    /**
     * EZDerm's order id that, with patient name, matches the case in their system
     */
    external_ezderm_order_id?: string | null;
    readonly finalize_assignment?: UserName;
    readonly finalized_report_id?: number;
    readonly prelim_report_id?: number;
    /**
     * date/time that we received a WPL order message indicating grossing was completed. This will match the order_message FK.
     */
    gross_completed?: string | null;
    readonly grossed_by_cert?: CliaCertificate;
    readonly id?: number;
    is_request_screen_complete?: boolean;
    is_screening?: boolean;
    is_manually_accessioned?: boolean | null;
    readonly lab_orders?: Array<LabOrder>;
    readonly ordering_doctor?: UserContact;
    patient: PatientFullInsurance;
    readonly physician_preferences?: string;
    priority?: CaseAdmin.priority;
    qa_reviews: Array<CaseQAReview>;
    /**
     * Changing this value will NOT modify the received_date for each specimen.
     */
    received_date?: string | null;
    readonly received_date_offset?: string;
    readonly received_date_timezone?: string;
    readonly report_id?: number;
    readonly report_signed_signature?: string;
    readonly screen_assignment?: UserName;
    slides: Array<CaseSlideAdmin>;
    /**
     * case origin, WPL or PW LIS
     */
    source?: CaseAdmin.source;
    specimens: Array<CaseSpecimenAdmin>;
    /**
     * current workflow status of the case
     */
    status?: CaseAdmin.status;
    readonly supervising_physician?: UserContact;
    readonly transmitting_at?: string;
    type?: CaseAdmin.type;
    readonly updated_at?: string;
    working_reasons: any;
    readonly finalizing_signature_id?: number;
    /**
     * True if we're billing for TC (technical component - lab) work, False if someone else bills.
     */
    bill_tc?: boolean;
    /**
     * True if we're billing for PC (professional component - pathologist) work, False if someone else bills.
     */
    bill_pc?: boolean;
    readonly biller?: Biller;
    screen_requests: Array<ScreenRequest>;
    readonly bill_caches?: Array<BillCacheAdmin>;
    /**
     * date/time that bill will be sent to biller
     */
    billing_date?: string | null;
    readonly has_biller_result_without_bill?: boolean;
    /**
     * This case is intended to be read on glass
     */
    glass_read?: boolean;
    readonly order_message_id?: number;
    readonly result_message_id?: number;
    event_log: Array<CaseEventLog>;
    status_log: Array<CaseStatusLog>;
    finalizing_signature: CaseReportSignature;
    readonly grossed_by_external_lab_id?: number;
    readonly billing_type_default?: string;
    readonly bill_tc_default?: string;
    readonly bill_pc_default?: string;
    readonly remains_digital_pc?: boolean;
    readonly data_entry?: CaseDataEntry;
    readonly data_entries?: Array<CaseDataEntry>;
    readonly specimen_bag_barcode?: string | null;
}

export namespace CaseAdmin {

    /**
     * case billing type: Third-Party Bill, Client Bill, Cash Pay, or No Bill
     */
    export enum billing_type {
        GLOBAL = 'global',
        CLIENT = 'client',
        NONE = 'none',
    }

    export enum priority {
        RUSH = 'rush',
        STAT = 'stat',
        NORMAL = 'normal',
    }

    /**
     * case origin, WPL or PW LIS
     */
    export enum source {
        WPL = 'wpl',
        PW = 'pw',
        APL = 'apl',
    }

    /**
     * current workflow status of the case
     */
    export enum status {
        ORDERED = 'ordered',
        ACTIVE = 'active',
        WORKING = 'working',
        FLAGGED = 'flagged',
        FINALIZED = 'finalized',
        DELETED = 'deleted',
        ARCHIVED = 'archived',
        LIS_PENDING = 'lis-pending',
        ERROR = 'error',
    }

    export enum type {
        CLINICAL = 'clinical',
        TEST = 'test',
        DEMO = 'demo',
        VALIDATION = 'validation',
        CONSULT = 'consult',
        CONTROL = 'control',
        TC_PC = 'tc-pc',
        HISTORICAL = 'historical',
        EDUCATION = 'education',
        NON_DIAGNOSTIC = 'non-diagnostic',
        MOHS_QC = 'mohs-qc',
        TRIAGE = 'triage',
    }


}
